import { TRIP_TYPE_CONFIG } from '@ixigo/cab-core/location';
import Close from '@ixigo/icons/Close';
import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import { BottomsheetExtended } from '@repo/shared/components';
import cn from '@repo/shared/utils/cn';
import { useEffect, useRef, useState } from 'react';
import type { FareDetailErrorType, FareDetailsErrorResponse } from '@/api';
import { getActionPopupBehavior, isRefetchActionType } from '@/api/fareDetails';
import type { ActionPopupAction } from '@/api/types';
import cabErrorImage from '@/assets/api-error.png?url';
import { NavigationHeader } from '@/components';
import {
	ActionPopupView,
	CabPicker,
	type CabPickerProps,
	type CabSelectionOptions,
	HighlightsRoute,
} from '@/components/cab-selection/';
import {
	FARE_DETAIL_ERROR_MAP,
	FareDetailView,
} from '@/components/error-states';
import type { SearchFormData } from '@/components/search-form';
import { useTracker } from '@/lib/analytics/provider';
import { useReviewPageActions } from '@/routes/review/-store';
import { useFareDetail } from './hooks';
import Loader from './loader';
import { getCabAttributes } from './utils';

const PartialPayNote = ({ note }: { note: string }) => (
	<Typography variant="body-xs" className="text-secondary text-center">
		{note}
	</Typography>
);

export interface CabSelectionPickerProps {
	showOptions: boolean;
	onClose: () => void;
	onConfirm: (cabId: string) => void;
	defaultCabOptions: CabSelectionOptions;
	selectedCabId?: string;
	burnOpted?: CabPickerProps['burnOpted'];
}

export function CabSelectionPicker({
	showOptions,
	onClose,
	onConfirm,
	defaultCabOptions,
	selectedCabId: defaultSelectedCabId,
	burnOpted,
}: CabSelectionPickerProps) {
	const { track } = useTracker();

	const exitCallbackRef = useRef<() => void>(null);
	const [selectedCabId, setSelectedCabId] = useState(
		defaultSelectedCabId || '',
	);
	const [showCabPicker, setShowCabPicker] = useState(showOptions || false);
	const { setSelectedCab } = useReviewPageActions();

	useEffect(() => {
		setShowCabPicker(showOptions);
		setSelectedCabId(defaultSelectedCabId || '');
	}, [showOptions, defaultSelectedCabId]);

	const handleClose = () => {
		onClose();
		setSelectedCabId(selectedCabId);
	};
	const handleConfirm = () => {
		handleClose();
		exitCallbackRef.current = () => {
			const selectedCab = defaultCabOptions.options.find(
				(cab) => cab.cabId === selectedCabId,
			);
			if (selectedCabId && selectedCab) {
				track('Cab Type Selected', {
					...getCabAttributes(selectedCab),
					Funnel: 'Cab',
				});
				setSelectedCab(selectedCab);
				onConfirm(selectedCabId);
			}
		};
	};
	const selectedCab = defaultCabOptions.options.find(
		(cab) => cab.cabId === selectedCabId,
	);

	return (
		<BottomsheetExtended
			open={showCabPicker}
			onClose={handleClose}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
				},
			}}
			classes={{
				container: 'h-auto pb-safe no-scrollbar',
			}}
		>
			<div className="flex flex-col">
				<NavigationHeader
					title="Choose a cab"
					className={cn(
						'border-b border-tertiary sticky top-0 z-[1] bg-primary rounded-t-[30px]',
					)}
					leftIcon={<Close />}
					leftAction={handleClose}
				/>

				<CabPicker
					onChange={(selectedCabId) => {
						setSelectedCabId(selectedCabId);
					}}
					cabSelectionOptions={defaultCabOptions}
					selectedCabId={selectedCabId}
					burnOpted={burnOpted}
				/>

				<div className="sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-2.5 flex flex-col items-center">
					<Button
						variant="solid"
						color="brand"
						size="lg"
						className="w-full"
						onClick={handleConfirm}
						disabled={!selectedCabId}
					>
						Confirm
					</Button>
					{selectedCab?.ctaNote && (
						<PartialPayNote note={selectedCab.ctaNote} />
					)}
				</div>
			</div>
		</BottomsheetExtended>
	);
}

export interface CabSelectionWithDataProps {
	showOptions: boolean;
	onClose: () => void;
	/**
	 * Confirm the selected cab. `acceptedActions` carries any action types the
	 * user accepted here (e.g. a near-airport confirmation) so the caller can
	 * forward them to the review fetch.
	 */
	onConfirm: (cabId: string, acceptedActions: string[]) => void;
	formData: Required<SearchFormData> | null;
	/**
	 * Called when the user picks the secondary "Edit" action on the near-airport
	 * popup, with the field to re-open the location picker on.
	 */
	onNearbyAirportEdit?: (field: 'pickup' | 'drop') => void;
}

export function CabSelectionWithData({
	showOptions,
	onClose,
	onConfirm,
	formData,
	onNearbyAirportEdit,
}: CabSelectionWithDataProps) {
	const { track } = useTracker();

	const exitCallbackRef = useRef<(() => void) | null>(null);
	const [selectedCabId, setSelectedCabId] = useState('');
	const [showCabPicker, setShowCabPicker] = useState(showOptions || false);
	// Action types accepted from a prior actionPopup — re-requests with these
	const [acceptedActions, setAcceptedActions] = useState<string[]>([]);
	const { setFareDetails, setSelectedCab } = useReviewPageActions();

	// The request is cancelled by useFareDetail/useApiData itself: closing the
	// sheet flips showCabPicker, which passes `null` formData here and aborts the
	// in-flight request on the dependency change.
	const { loading, error, fareDetailsResponse, actionPopup } = useFareDetail(
		showCabPicker && formData ? formData : null,
		{
			actions: acceptedActions,
		},
	);

	const cabOptions: CabSelectionOptions | undefined =
		fareDetailsResponse?.tripDetails?.cabSelection;

	useEffect(() => {
		setShowCabPicker(showOptions);
		setSelectedCabId('');
		setAcceptedActions([]);
	}, [showOptions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: setFareDetails is an action
	useEffect(() => {
		if (showCabPicker && cabOptions && Object.keys(cabOptions).length > 0) {
			setFareDetails(fareDetailsResponse);
		}
	}, [cabOptions, fareDetailsResponse]);

	const handleClose = () => {
		onClose();
	};

	// "Edit" — re-open the picker on the non-airport side (the one near an airport)
	const reopenPickerForEdit = () => {
		if (formData && onNearbyAirportEdit) {
			const { airportField } = TRIP_TYPE_CONFIG[formData.tripType];
			onNearbyAirportEdit(airportField === 'pickup' ? 'drop' : 'pickup');
		}
	};

	// Both popup buttons route through the action→behavior map, so adding a new
	// action type is a single map entry rather than new branching here. The view
	// hands back the chosen button's action; we resolve its behavior.
	const handlePopupAction = (action: ActionPopupAction['action']) => {
		const type = action?.type;
		// Refetch actions echo the type back; isRefetchActionType also narrows it.
		if (isRefetchActionType(type)) {
			setAcceptedActions([type]);
			return;
		}
		if (getActionPopupBehavior(type) === 'edit') {
			handleClose();
			reopenPickerForEdit();
			return;
		}
		handleClose();
	};

	const handleErrorAction = (_type: FareDetailErrorType) => {
		setShowCabPicker(false);
		exitCallbackRef.current = () => {
			onClose();
		};
	};
	const getErrorProps = (errorObj: FareDetailsErrorResponse) => {
		const { message, title, type } = errorObj.errors;
		const errorMap = {
			...FARE_DETAIL_ERROR_MAP,
			GENERIC_ERROR: { image: cabErrorImage, btnText: 'Try Again' },
		};

		return {
			title,
			description: message,
			errorType: type,
			errorMap,
			action: () => handleErrorAction(type),
		};
	};

	const handleConfirm = () => {
		handleClose();
		exitCallbackRef.current = () => {
			const selectedCab = cabOptions.options.find(
				(cab) => cab.cabId === selectedCabId,
			);

			if (selectedCabId && selectedCab) {
				track('Cab Type Selected', {
					...getCabAttributes(selectedCab),
					Funnel: 'Cab',
				});
				setSelectedCab(selectedCab);
				onConfirm(selectedCabId, acceptedActions);
			}
		};
	};
	const selectedCab = cabOptions?.options.find(
		(cab) => cab.cabId === selectedCabId,
	);
	// A server-driven popup replaces the cab list (no cabs in this response)
	const showConfirmation = !loading && !error && !!actionPopup;
	const showPartialPayNote =
		showCabPicker && !loading && !error && !showConfirmation && !!selectedCab;
	const renderContent = () => {
		if (error) {
			const { title, description, errorType, errorMap, action } =
				getErrorProps(error);
			return (
				<FareDetailView
					title={title}
					description={description}
					errorType={errorType}
					onAction={action}
					fullScreen={false}
					errorMap={errorMap}
				/>
			);
		}
		if (showConfirmation && actionPopup) {
			return (
				<ActionPopupView popup={actionPopup} onAction={handlePopupAction}>
					{actionPopup.highlights && (
						<HighlightsRoute highlights={actionPopup.highlights} />
					)}
				</ActionPopupView>
			);
		}
		// No cabs yet (e.g. while the sheet animates closed, or a confirmation
		// response with no cab list) — CabPicker requires defined options
		if (!cabOptions) {
			return null;
		}
		return (
			<CabPicker
				onChange={(selectedCabId) => {
					setSelectedCabId(selectedCabId);
				}}
				cabSelectionOptions={cabOptions}
				selectedCabId={selectedCabId}
			/>
		);
	};

	return (
		<BottomsheetExtended
			open={showCabPicker}
			onClose={handleClose}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
				},
			}}
			classes={{
				container: 'h-auto pb-safe no-scrollbar',
			}}
		>
			<div className="flex flex-col">
				<NavigationHeader
					title={
						loading
							? 'Fetching Cabs...'
							: showConfirmation
								? (actionPopup?.title ?? '')
								: 'Choose a cab'
					}
					className={cn(
						'border-b border-tertiary sticky top-0 z-[1] bg-primary rounded-t-[30px]',
						error && 'hidden',
					)}
					leftIcon={<Close />}
					leftAction={handleClose}
				/>
				{loading ? <Loader /> : renderContent()}
				<div
					className={cn(
						'sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-2.5 flex flex-col items-center',
						(error || showConfirmation) && 'hidden',
					)}
				>
					<Button
						variant="solid"
						color="brand"
						size="lg"
						className="w-full"
						onClick={handleConfirm}
						disabled={!selectedCabId || loading}
					>
						Confirm
					</Button>
					{showPartialPayNote && selectedCab.ctaNote && (
						<PartialPayNote note={selectedCab.ctaNote} />
					)}
				</div>
			</div>
		</BottomsheetExtended>
	);
}

export default CabSelectionPicker;
